import React, { useEffect, useState, useCallback } from 'react';
import { IPermissionsEditProps } from './PermissionsEdit.types';
import { IPermissionDetail } from '../../../services/rolesAndPermissions/rolesAndPermissions.types';
import { ModalWithButtons } from '../../ModalWithButtons/ModalWithButtons.component';
import { useLayoutContext } from '../../../Context/Layout.context';
import { Grid } from '@material-ui/core';
import { VerticalTabs } from '../../Tabs/VerticalTabs/VerticalTabs.component';
import { TabItem } from '../../Tabs/VerticalTabs/VerticalTabs.types';
import { useStyles } from './PermissionsEdit.styles';
import DefaultRoleIcon from '../../../assets/icons/default-role.svg';
import { PermissionsList } from './PermissionsList.component';
import { PermissionActivity } from './PermissionsList.types';
import { getActivityLabel, comparePermissionsByName } from './helpers';
import _ from 'lodash';
import { SearchInput } from '../../SearchInput/SearchInput.component';
import Typography from '@material-ui/core/Typography';
import TooltipOne from '../../TooltipOne/TooltipOne.component';
import { LeavePrompt } from '../../Prompt/LeavePrompt/LeavePrompt.component';
import { setRoleWithPermissions } from '../../../services/rolesAndPermissions/rolesAndPermissions.service';

enum TabKey {
  active,
  inActive,
  all,
  search,
}

export const PermissionsEdit: React.FC<IPermissionsEditProps> = ({
  roleId,
  roleWithPermissions,
  onClose,
  onUpdate,
}) => {
  const classes = useStyles();
  const [permissions, setPermissions] = useState<IPermissionDetail[]>(_.cloneDeep(roleWithPermissions.permissions));
  const [activePermissions, setActivePermissions] = useState<IPermissionDetail[]>([]);
  const [filteredPermissions, setFilteredPermissions] = useState<IPermissionDetail[]>([]);
  const [changedToActive, setChangedToActive] = useState<string[]>([]);
  const [changedToInActive, setChangedToInActive] = useState<string[]>([]);
  const [tabItems, setTabItems] = useState<TabItem[]>([]);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);
  const [searchPhrase, setSearchPhrase] = useState<string>('');
  const [currentActivity, setCurrentActivity] = useState<PermissionActivity | undefined>(PermissionActivity.active);
  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState<boolean>(false);
  const [filterResultLabel, setFilterResultLabel] = useState<string>('');
  const { showLoader, openAlert } = useLayoutContext();

  const togglePermissionActivity = (permissionId: string) => {
    const newPermisions: IPermissionDetail[] = _.cloneDeep(permissions).map((permission: IPermissionDetail) => {
      if (permission.id === permissionId) {
        if (permission.isActive) {
          if (changedToActive.includes(permissionId)) {
            setChangedToActive(_.without(changedToActive, permissionId));
          } else {
            setChangedToInActive([...changedToInActive, permissionId]);
          }
        } else {
          if (changedToInActive.includes(permissionId)) {
            setChangedToInActive(_.without(changedToInActive, permissionId));
          } else {
            setChangedToActive([...changedToActive, permissionId]);
          }
        }
        permission.isActive = !permission.isActive;
      }
      return permission;
    });
    setPermissions(newPermisions.sort(comparePermissionsByName));
  };

  useEffect(() => {
    setIsDataChanged(JSON.stringify(permissions) !== JSON.stringify(roleWithPermissions.permissions));
  }, [permissions]);

  const clearData = () => {
    setPermissions(_.cloneDeep(roleWithPermissions.permissions));
    setIsDataChanged(false);
    setSearchPhrase('');
    setCurrentActivity(PermissionActivity.active);
  };
  const handleCancel = () => {
    if (!isDataChanged) {
      onClose && onClose();
    } else {
      setIsLeavePromptOpen(true);
    }
  };

  const handleSave = () => {
    showLoader(true, 'Aktualizuję dane');
    setRoleWithPermissions(roleId, changedToActive, changedToInActive)
      .then(
        () => {
          openAlert('success');
          onUpdate && onUpdate();
        },
        () => {
          openAlert('save_error');
          clearData();
        },
      )
      .finally(() => {
        showLoader(false);
      });
  };

  const getPermissionsLenght = (activity: PermissionActivity): number => {
    switch (activity) {
      case PermissionActivity.active:
        return activePermissions.length;
      case PermissionActivity.inActive:
        return permissions.length - activePermissions.length;
      case PermissionActivity.all:
        return permissions.length;
      default:
        return 0;
    }
  };

  const getTabLabel = (activity: PermissionActivity) => {
    const permissionsListLength = getPermissionsLenght(activity);
    return `${getActivityLabel(activity)} (${permissionsListLength})`;
  };

  const getActiveTabLabel = useCallback(() => getTabLabel(PermissionActivity.active), [activePermissions]);

  const getInActiveTabLabel = useCallback(
    () => getTabLabel(PermissionActivity.inActive),
    [activePermissions, permissions],
  );

  const getAllTabLabel = useCallback(() => getTabLabel(PermissionActivity.all), [permissions]);

  const filterPermissions = (phrase: string, permissions: IPermissionDetail[]) => {
    if (phrase) {
      const phraseLower = phrase.toLowerCase();
      const results = permissions.filter((permission: IPermissionDetail) =>
        permission.name?.toLowerCase().includes(phraseLower),
      );
      setFilteredPermissions(results);
      return results;
    }
    return [];
  };

  const getCurrentPermissions = (activity: PermissionActivity): IPermissionDetail[] => {
    switch (activity) {
      case PermissionActivity.active:
        return activePermissions;
      case PermissionActivity.inActive:
        return filterPermissions(searchPhrase, _.difference(permissions, activePermissions));
      case PermissionActivity.all:
        return permissions;
      default:
        return activePermissions;
    }
  };

  const handleSearchPermission = useCallback(
    _.debounce((phrase: string) => {
      setSearchPhrase(phrase);
    }, 1000),
    [],
  );

  useEffect(() => {
    const newActivePermissions: IPermissionDetail[] = permissions.filter((item: IPermissionDetail) => item.isActive);
    setActivePermissions(newActivePermissions.sort(comparePermissionsByName));
  }, [permissions]);

  const getActivityFromTab = (tabKey: TabKey): PermissionActivity | undefined => {
    switch (tabKey) {
      case TabKey.active:
        return PermissionActivity.active;
      case TabKey.inActive:
        return PermissionActivity.inActive;
      case TabKey.all:
        return PermissionActivity.all;
      default:
        return undefined;
    }
  };

  const getTabFromActivity = (activity: PermissionActivity | undefined): TabKey => {
    switch (activity) {
      case PermissionActivity.active:
        return TabKey.active;
      case PermissionActivity.inActive:
        return TabKey.inActive;
      case PermissionActivity.all:
        return TabKey.all;
      default:
        return TabKey.search;
    }
  };

  const handleChangeTab = useCallback(
    (tabId: number) => {
      setCurrentActivity(getActivityFromTab(tabId as TabKey));
      setFilteredPermissions([]);
    },
    [searchPhrase, permissions],
  );

  useEffect(() => {
    setSearchPhrase('');
  }, [currentActivity]);

  useEffect(() => {
    const items = [
      {
        label: 'active',
        getInteractiveLabel: getActiveTabLabel,
        children: (
          <PermissionsList
            permissions={searchPhrase ? filterPermissions(searchPhrase, activePermissions) : activePermissions}
            onTogglePermissionActivity={togglePermissionActivity}
            withSearchResults
          />
        ),
      },
      {
        label: 'inactive',
        getInteractiveLabel: getInActiveTabLabel,
        children: (
          <PermissionsList
            permissions={
              searchPhrase
                ? filterPermissions(searchPhrase, _.difference(permissions, activePermissions))
                : _.difference(permissions, activePermissions)
            }
            onTogglePermissionActivity={togglePermissionActivity}
            withSearchResults
          />
        ),
      },
      {
        label: 'all',
        getInteractiveLabel: getAllTabLabel,
        children: (
          <PermissionsList
            permissions={searchPhrase ? filterPermissions(searchPhrase, permissions) : permissions}
            onTogglePermissionActivity={togglePermissionActivity}
            withSearchResults
          />
        ),
      },
    ];
    setTabItems(items);
  }, [activePermissions, permissions, searchPhrase]);

  useEffect(() => {
    if (!searchPhrase || currentActivity === undefined) return;
    const permissionsListLength = getPermissionsLenght(currentActivity);
    const filteredPermissionsLenght = filterPermissions(searchPhrase, getCurrentPermissions(currentActivity)).length;
    const label = `Znaleziono ${filteredPermissionsLenght} z ${permissionsListLength}`;
    setFilterResultLabel(label);
  }, [searchPhrase, currentActivity]);

  return (
    <>
      <ModalWithButtons
        xCloseButton={handleCancel}
        id={roleId}
        open={true}
        size="md"
        title={
          <>
            {roleWithPermissions.name}
            {roleWithPermissions.isDefault &&
              <TooltipOne title="Rola domyślna" placement="top">
                <div className={classes.iconCircle}>
                  <img className={classes.defaultRoleIcon} src={DefaultRoleIcon} aria-label="Rola domyślna" />
                </div>
              </TooltipOne>
            }
          </>
        }
        subtitle={roleWithPermissions.description}
        onCancel={handleCancel}
        onConfirm={handleSave}
        confirmButtonDisabled={!isDataChanged}
        buttonsContainerStyle={{ margin: '10px 0 0 0' }}
      >
        <Grid container>
          <VerticalTabs
            items={tabItems}
            style={{ height: '400px', width: '200px' }}
            selectedTab={searchPhrase !== '' ? TabKey.search : getTabFromActivity(currentActivity)}
            onChange={handleChangeTab}
            tabPanelHeader={
              <div className={classes.listHeader}>
                <Typography className={classes.tabTitle}>
                  {!searchPhrase && currentActivity !== undefined ? (
                    <>{getTabLabel(currentActivity)}</>
                  ) : (
                    <>{filterResultLabel}</>
                  )}
                </Typography>
                <div className={classes.searchBar}>
                  <SearchInput
                    onSearch={handleSearchPermission}
                    searchInput={searchPhrase}
                    placeholder="Wyszukaj uprawnienia"
                    inputStyle={{ width: '300px', fontSize: '14px' }}
                    activeStyle={{ border: '1px solid #303E69' }}
                  />
                </div>
              </div>
            }
          />
        </Grid>
      </ModalWithButtons>
      {isLeavePromptOpen && (
        <LeavePrompt
          isOpen={true}
          onConfirm={() => onClose && onClose()}
          onCancel={() => setIsLeavePromptOpen(false)}
        />
      )}
    </>
  );
};
